import { lazy } from "react";
import { RequirePermission } from "../components/RequirePermission";
import { SettingsIndex, SettingsLayout } from "../features/settings/SettingsLayout";
import { Navigate, Route } from "react-router";
import { ContactsRedirect } from "../features/contacts/ContactsRedirect";
import { StoreRedirect } from "../features/store/StoreRedirect";

const ServicesRequestPage = lazy(() =>
  import("../pages/settings/services").then((m) => ({ default: m.ServicesRequestPage })),
);
const BillingSettingsPage = lazy(() =>
  import("../features/billing/BillingSettingsPage").then((m) => ({
    default: m.BillingSettingsPage,
  })),
);
const BranchesPage = lazy(() =>
  import("../features/settings/branches/BranchesPage").then((m) => ({ default: m.BranchesPage })),
);
const ApiKeysPage = lazy(() =>
  import("../features/api-keys/ApiKeysPage").then((m) => ({ default: m.ApiKeysPage })),
);
const ApiPlaygroundPage = lazy(() =>
  import("../features/api-keys/ApiPlaygroundPage").then((m) => ({ default: m.ApiPlaygroundPage })),
);
const WebhooksPage = lazy(() =>
  import("../features/webhooks/WebhooksPage").then((m) => ({ default: m.WebhooksPage })),
);
const ConnectionDetailPage = lazy(() =>
  import("../features/commerce/ConnectionsPage").then((m) => ({ default: m.ConnectionDetailPage })),
);
const CollisionsList = lazy(() =>
  import("../features/commerce/sync-health/collisions").then((m) => ({
    default: m.CollisionsList,
  })),
);
const ShopifyConnectWizard = lazy(() =>
  import("../features/commerce/connect/shopify").then((m) => ({ default: m.ShopifyConnectWizard })),
);
const CustomApiConnectPage = lazy(() =>
  import("../features/commerce/connect/custom-api").then((m) => ({
    default: m.CustomApiConnectPage,
  })),
);
const ZidConnectWizard = lazy(() =>
  import("../features/commerce/connect/zid").then((m) => ({ default: m.ZidConnectWizard })),
);
const WooConnectWizard = lazy(() =>
  import("../features/commerce/connect/woocommerce").then((m) => ({ default: m.WooConnectWizard })),
);
const CustomCsvImportPage = lazy(() =>
  import("../features/commerce/connect/custom-csv").then((m) => ({
    default: m.CustomCsvImportPage,
  })),
);
const TeamsPage = lazy(() =>
  import("../features/settings/teams/TeamList").then((m) => ({ default: m.TeamsPage })),
);
const ByokKeyManager = lazy(() =>
  import("../features/settings/ai/ByokKeyManager").then((m) => ({ default: m.ByokKeyManager })),
);
const AuditLogPage = lazy(() =>
  import("../features/audit-log/AuditLogPage").then((m) => ({ default: m.AuditLogPage })),
);
const RolesPage = lazy(() =>
  import("../features/roles/RolesPage").then((m) => ({ default: m.RolesPage })),
);
const WorkspaceSettingsPage = lazy(() =>
  import("../features/settings/workspace/WorkspaceSettingsPage").then((m) => ({
    default: m.WorkspaceSettingsPage,
  })),
);
const DocumentDesignPage = lazy(() =>
  import("../features/settings/documents/DocumentDesignPage").then((m) => ({
    default: m.DocumentDesignPage,
  })),
);
const TeamPage = lazy(() =>
  import("../features/team/TeamPage").then((m) => ({ default: m.TeamPage })),
);
const ApprovalQueue = lazy(() =>
  import("../features/team/ApprovalQueue").then((m) => ({ default: m.ApprovalQueue })),
);

const OtpPage = lazy(() => import("../features/otp/OtpPage").then((m) => ({ default: m.OtpPage })));
const TemplateGallery = lazy(() =>
  import("../features/integrations/automations/TemplateGallery").then((m) => ({
    default: m.TemplateGallery,
  })),
);
const CredentialsPage = lazy(() =>
  import("../features/settings/integrations/credentials/CredentialsPage").then((m) => ({
    default: m.CredentialsPage,
  })),
);
const NotificationPreferencesPage = lazy(() =>
  import("../pages/settings/notifications").then((m) => ({
    default: m.NotificationPreferencesPage,
  })),
);
const MenuSettingsPage = lazy(() =>
  import("../pages/settings/menu-settings").then((m) => ({ default: m.MenuSettingsPage })),
);
const RestaurantStatusRulesPage = lazy(() =>
  import("../pages/settings/restaurant-status-rules").then((m) => ({
    default: m.RestaurantStatusRulesPage,
  })),
);
const FeatureSwitchesPage = lazy(() =>
  import("../pages/settings/feature-switches").then((m) => ({ default: m.FeatureSwitchesPage })),
);
const BalanceFollowUpsPage = lazy(() =>
  import("../pages/settings/balance-followups").then((m) => ({ default: m.BalanceFollowUpsPage })),
);
const SmsFallback = lazy(() =>
  import("../features/settings/channels/sms-fallback").then((m) => ({ default: m.SmsFallback })),
);
const GoogleIntegrationsPage = lazy(() =>
  import("../features/settings/integrations/google/GoogleIntegrationsPage").then((m) => ({
    default: m.GoogleIntegrationsPage,
  })),
);
const SecurityPage = lazy(() =>
  import("../features/settings/security/SecurityPage").then((m) => ({ default: m.SecurityPage })),
);
const WorkspaceHoursPage = lazy(() =>
  import("../features/settings/workspace/WorkspaceSubPages").then((m) => ({
    default: m.WorkspaceHoursPage,
  })),
);
const WorkspaceModulesPage = lazy(() =>
  import("../features/settings/workspace/WorkspaceSubPages").then((m) => ({
    default: m.WorkspaceModulesPage,
  })),
);
const AiBehaviorPage = lazy(() =>
  import("../features/settings/workspace/WorkspaceSubPages").then((m) => ({
    default: m.AiBehaviorPage,
  })),
);

/** Route table. The router itself (`BrowserRouter` / `MemoryRouter`) wraps `<App>`. */

// Route order inside <Route path="/w/:workspaceId"> is preserved: this fragment slots in where it was inline.
export const workspaceSettingsRoutes = (
  <>
    <Route path="settings" element={<SettingsLayout />}>
      <Route index element={<SettingsIndex />} />
      <Route path="services" element={<ServicesRequestPage />} />
      <Route
        path="teams"
        element={
          <RequirePermission anyOf={["team.view"]}>
            <TeamsPage />
          </RequirePermission>
        }
      />
      <Route path="ai/byok" element={<ByokKeyManager />} />
      <Route path="workspace" element={<WorkspaceSettingsPage />} />
      <Route path="workspace/hours" element={<WorkspaceHoursPage />} />
      <Route path="workspace/modules" element={<WorkspaceModulesPage />} />
      <Route path="ai/behavior" element={<AiBehaviorPage />} />
      <Route path="security" element={<SecurityPage />} />
      <Route path="otp" element={<OtpPage />} />
      <Route path="notifications" element={<NotificationPreferencesPage />} />
      <Route path="menu" element={<MenuSettingsPage />} />
      <Route path="order-status-messages" element={<RestaurantStatusRulesPage />} />
      <Route path="features" element={<FeatureSwitchesPage />} />
      <Route path="balance-followups" element={<BalanceFollowUpsPage />} />
      <Route path="channels/sms-fallback" element={<SmsFallback />} />
      <Route path="integrations/google" element={<GoogleIntegrationsPage />} />
      <Route path="integrations/automations" element={<TemplateGallery />} />
      <Route path="integrations/credentials" element={<CredentialsPage />} />
      {/* OWN-361: old links keep working */}
      <Route path="integrations/hub" element={<Navigate to="../integrations/google" replace />} />
      <Route
        path="integrations/google-sheets"
        element={<Navigate to="../integrations/google?tab=sheets" replace />}
      />
      <Route
        path="integrations/doc-templates"
        element={<Navigate to="../integrations/google?tab=docs" replace />}
      />
      <Route path="documents" element={<DocumentDesignPage />} />
      <Route
        path="team"
        element={
          <RequirePermission anyOf={["team.view"]} reasonKey="team.forbidden">
            <TeamPage />
          </RequirePermission>
        }
      />
      <Route path="account" element={<Navigate to="../team" replace />} />
      <Route
        path="approvals"
        element={
          <RequirePermission anyOf={["team.view"]}>
            <ApprovalQueue />
          </RequirePermission>
        }
      />
      <Route path="roles" element={<RolesPage />} />
      <Route path="labels" element={<ContactsRedirect />} />
      <Route path="custom-fields" element={<ContactsRedirect dialog="fields" />} />
      <Route path="api-keys" element={<ApiKeysPage />} />
      <Route path="api-keys/playground" element={<ApiPlaygroundPage />} />
      <Route path="integrations/webhooks" element={<WebhooksPage />} />
      <Route path="branches" element={<BranchesPage />} />
      <Route path="commerce" element={<StoreRedirect to="store/settings" />} />
      <Route path="commerce/collisions" element={<CollisionsList />} />
      <Route path="commerce/connect/custom-api" element={<CustomApiConnectPage />} />
      <Route path="commerce/connect/zid" element={<ZidConnectWizard />} />
      <Route path="commerce/connect/woocommerce" element={<WooConnectWizard />} />
      <Route path="commerce/connect/shopify" element={<ShopifyConnectWizard />} />
      <Route path="commerce/connect/custom-csv" element={<CustomCsvImportPage />} />
      <Route path="commerce/:connectionId" element={<ConnectionDetailPage />} />
      <Route path="audit-log" element={<AuditLogPage />} />
      <Route
        path="billing"
        element={
          <RequirePermission anyOf={["billing.view"]}>
            <BillingSettingsPage />
          </RequirePermission>
        }
      />
    </Route>
  </>
);
